import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { ToastrService } from 'ngx-toastr';
import { ButtonIconProps, ButtonProps } from '@yooga-tecnologia/mantra/loader';

import { CartService } from '../../../domain/services/cart.service';
import { UserService } from '../../../domain/services/user.service';
import { MenuApiService } from '../../../services/menu/menu-api';
import { MenuCategoryDto, MenuItemDto, MenuResponseDto } from '../../../services/menu/dto/api-response-dto';
import { TableService } from '../../../domain/services/table.service';
import { TableApiService } from '../../../services/tables/table-api.service';
import { SocketService } from '../../../services/socket/socket.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { EventTrackingEnum, PageStatusEnum, SCREEN_VIEW_TIMEOUT_IN_MS } from '../../../app.types';
import { CurrencyPipe } from '../../../pipes/currency.pipe';
import { Table } from '../../../domain/models/table';
// COMPONENTS
import { AppBarComponent } from '../../../components/app-bar/app-bar.component';
import { LoadingComponent } from '../../../components/loading/loading.component';
import { ProductListItemComponent } from './product-list-item/product-list-item.component';
import { FeaturedItemsComponent } from './featured-items/featured-items.component';
import { EventService } from '../../../domain/services/event.service';
import { ConfigService } from '../../../services/config.service';
import { Subscription } from 'rxjs';

export type CloseType = 'TOTAL' | 'PARTIAL' | 'CUSTOM';

@Component({
  selector: 'app-store-menu',
  standalone: true,
  imports: [
    CommonModule,
    AppBarComponent,
    ProductListItemComponent,
    FeaturedItemsComponent,
    CurrencyPipe,
    LoadingComponent,
  ],
  providers: [
    MenuApiService,
    NgxSmartModalService,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StoreMenuComponent implements OnInit, OnDestroy {
  uuid: string = '';
  table: Table;
  // storeId?: string;
  storeMenuCategories: MenuCategoryDto[] = [];
  featuredItems: MenuItemDto[] = [];
  pageStatus: PageStatusEnum = PageStatusEnum.LOADING;
  pageStatusEnum = PageStatusEnum;
  backButtonProps: ButtonIconProps = {
    icon: 'arrow-left',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  activeCategoryId: number | null = null;
  private intersectionObserver?: IntersectionObserver;
  private readonly APP_BAR_HEIGHT = 56;
  private readonly SCROLL_OFFSET = 80;

  productModal!: NgxSmartModalComponent;
  cart: any;

  cartTotal: number = 0;
  qtyItemsAddedToCart: number = 0;
  screenViewTimeout: any;
  submittingOrder: boolean = false;

  isTabletLandscape: boolean = false;
  private callingWaiter: boolean = false;
  callWaiterButtonProps: ButtonProps = {
    label: 'Chamar garçom',
    color: 'secondary',
    variant: 'emphasis',
    size: 'large',
  };

  constructor(
    private cartService: CartService,
    private menuApiService: MenuApiService,
    private localStorageService: LocalStorageService,
    private tableService: TableService,
    private tableApiService: TableApiService,
    private socketService: SocketService,
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventService,
    private vcr: ViewContainerRef,
    private userService: UserService,
    private toastr: ToastrService,
    private configService: ConfigService,
  ) {
    this.cart = this.cartService.getCart();
    this.table = this.tableService.getTable();
    this.uuid = String(this.route.snapshot.paramMap.get('uuid'));
    this.pageStatus = PageStatusEnum.LOADING;
  }

  ngOnInit(): void {
    this.updateTabletLandscape();

    if(!this.table){
      this.handleBackButtonClick();
      return;
    }
    this.menuApiService.getMenu(this.table.tableId).subscribe({
      next: (response: MenuResponseDto) => {
        if (response.data) {
          let menu = response.data.categories as MenuCategoryDto[];
          this.storeMenuCategories = this.filterMenuBySchedule(menu);
          this.setFeaturedItems(this.storeMenuCategories);
          this.pageStatus = PageStatusEnum.SUCCESS;
          this.localStorageService.setItem('storeMenuCategories', menu, LocalStorageService.timeToMs.hours(24));

          if (!this.activeCategoryId && this.storeMenuCategories.length > 0) {
            this.activeCategoryId = this.storeMenuCategories[0].id;
            this.scrollToCategory(this.activeCategoryId);
          }

          this.screenViewTimeout = setTimeout(() => {
            this.eventService.track(EventTrackingEnum.CHECKOUT_MENU_VIEWED_PAGE);
          }, SCREEN_VIEW_TIMEOUT_IN_MS);
        }
      },
      error: (error: any) => {
        console.error('[STORE MENU] Erro ao carregar menu:', error);
        // Não tem como carregar o menu, redireciona para a página inicial
        this.handleBackButtonClick();
      }
    });
    this.cartService.cart$.subscribe((cart: any[]) => {
      this.cart = cart;
      this.cartTotal = this.cartService.getCartTotal();
      this.qtyItemsAddedToCart = this.cartService.getQtyItemsAddedToCart();
    });

    this.cartService.qtyItemsAddedToCart$.subscribe((qty: number) => {
      this.qtyItemsAddedToCart = qty;
    });
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.screenViewTimeout) {
      clearTimeout(this.screenViewTimeout);
    }
  }

  handleBackButtonClick(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  private isAvailableToday(scheduleAvailable: string): boolean {
    const today = new Date().getDay(); // 0 = Domingo, 6 = Sábado
    return scheduleAvailable != '' && scheduleAvailable[today] === '1';
  }

  private setFeaturedItems(categories: MenuCategoryDto[]): void {
    this.featuredItems = [];
    for (const category of categories) {
      for (const item of category.items) {
        if (item.featured) {
          this.featuredItems.push(item);
        }
      }
    }
  }

  private filterMenuBySchedule(categories: MenuCategoryDto[]): MenuCategoryDto[] {
    return categories
      .filter(category => this.isAvailableToday(category.schedule_available))
      .map(category => ({
        ...category,
        items: category.items.filter(item => this.isAvailableToday(item.schedule_available))
      }))
      .filter(category => category.items.length > 0);
  }

  scrollToCategory(categoryId: number): void {
    const elementId = `product-category-${categoryId}`;
    const categoryElement = document.getElementById(elementId);
    if (!categoryElement) {
      return;
    }
    console.log(`Scrolling to category ${categoryId} (element ID: ${elementId})`);
    this.activeCategoryId = categoryId;

    const scrollContainer = this.getScrollContainer(categoryElement);
    if (scrollContainer) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const elementTop = categoryElement.getBoundingClientRect().top;
      const offsetPosition = scrollContainer.scrollTop + (elementTop - containerTop) - this.SCROLL_OFFSET;
      scrollContainer.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      const offsetPosition = categoryElement.getBoundingClientRect().top + window.pageYOffset - this.SCROLL_OFFSET;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Sobe na árvore a partir do elemento procurando o ancestral que realmente
   * possui rolagem vertical. Retorna null quando quem rola é a própria window
   * (caso do mobile/retrato, onde .page-content não tem overflow).
   */
  private getScrollContainer(element: HTMLElement): HTMLElement | null {
    let current = element.parentElement;
    while (current) {
      const { overflowY } = window.getComputedStyle(current);
      const canScroll = (overflowY === 'auto' || overflowY === 'scroll')
        && current.scrollHeight > current.clientHeight;
      if (canScroll) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  get displayCart(): any[] {
    return (this.cart ?? []).filter((item: any) => item.name !== 'Taxa de Serviço');
  }

  handleQuantityChange(item: any, event: any): void {
    const newQty = Number(event.detail.value);
    this.cartService.editItemQuantity(item.cartItemId, newQty > 99 ? 99 : newQty);
  }

  openProductModal(product: any): void {
    const productModal = this.cartService.createProductModal(
      product,
      this.vcr,
    );
    productModal.open();
  }

  navigateToCart(): void {
    this.router.navigate([`${this.uuid}/pedido`], { state: { cart: this.cart } });
  }

  handleSubmitOrder(): void {
    if (this.submittingOrder || !this.cart?.length) {
      return;
    }

    this.submittingOrder = true;

    const user = this.userService.getUser();
    const clientId = this.userService.checkUser() === 'AUTHORIZED' && user ? user.clientId : 0;
    const userId = this.userService.checkUser() === 'AUTHORIZED' && user ? user.id : undefined;

    this.eventService.track(EventTrackingEnum.CHECKOUT_CART_CLICKED_CREATE_ORDER);

    this.cartService.submitOrder({
      tableId: this.tableService.getTable().tableId,
      clientId,
      userId,
      idi: this.cart[0].idi,
    }).subscribe({
      next: () => {
        this.router.navigate([`${this.uuid}/pedido`], { state: { orderComplete: true } });
      },
      error: (error) => {
        console.error('[STORE MENU] Error submitting order:', error);
        this.submittingOrder = false;
        this.toastr.error('Erro ao enviar pedido', 'Erro');
      },
    });
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onViewportChange(): void {
    this.updateTabletLandscape();
  }

  private updateTabletLandscape(): void {
    this.isTabletLandscape = this.configService.isLandscapeTabletEnabled();
  }

  handleCallWaiter(): void {
    const table = this.tableService.getTable();
    if (this.callingWaiter || !table) {
      return;
    }

    this.callingWaiter = true;

    this.tableApiService.callWaiter(table.tableId).subscribe({
      next: () => {
        this.socketService.emit(
          'bot:update',
          JSON.stringify({
            table: String(table.tableId),
          }),
        );
        this.socketService.emit(
          'mesa:update',
          JSON.stringify({
            uuid: crypto.randomUUID(),
          }),
        );
        this.eventService.track(EventTrackingEnum.CHECKOUT_ORDER_SUMMARY_CLICKED_CALL_WAITER);
        this.callingWaiter = false;
        this.toastr.success('Garçom chamado! Em breve ele irá até sua mesa.', '', {
          toastClass: 'custom-toast-success',
          timeOut: 2500,
          closeButton: false,
        });
      },
      error: (error) => {
        console.error('[STORE MENU] Error calling waiter:', error);
        this.callingWaiter = false;
        this.toastr.error('Erro ao chamar garçom', 'Erro');
      },
    });
  }
}
